import {HeaderTitle} from '@/components/atoms'
import { LinkText } from '@/components/atoms'
import {ButtonIcon} from 'pepsico-ds'
import CustomDataTable from '@/components/organisms/CustomDataTable/CustomDataTable' 
import {getCellDataMappedCols} from '@/helpers/dataTableCellDataMapper'
import {useItemList} from '@/hooks/useItemList'
import {useNavigate} from 'react-router-dom'
import {useState} from 'react'
import {downloadDataAsCSVFile} from '@/helpers/downloadDataAsCSVFile'
import { getDateWithTimeString } from "@/helpers/shadcn/utils";
import {useAppDispatch} from '@/store'
import { setItemDetail } from '@/store/slices/itemSlice'
import './onlineItemsList.scss'

const getHeaderInfo = colConfigData => {
  const headerLabelList = []
  if (colConfigData?.length > 0) {
    colConfigData?.forEach(element => {
      headerLabelList.push(element?.label_of_column?.toUpperCase())
    })
  }

  return {headerLabelList}
}

const getDataToDownload = (colConfigData, dataList) => {
  const {headerLabelList = []} = getHeaderInfo(colConfigData)

  // To get the table row content based on column configured
  const rowData = []
  dataList?.forEach(element => {
    let tmpElement = {...element}
    if (tmpElement?.id) {
      delete tmpElement?.id
    }
    rowData.push(Object.values(tmpElement))
  })

  return [headerLabelList, ...rowData].join('\n')
}

const createCellDataMapperObj = (navigate, OrderDetail, dispatch, orderId) => ({
  item: {
    cellData: ({ row: { original } = {} }) => (
      <LinkText
        text={original?.item}
        id={`${original?.item} ${original?.item}`}
        handleClick={() => {
          navigate(`/orders/item/${original?.item}`, {
            state: { itemDetails: original }, // Pass object with navigate
          });
        }}
      />
    ),
    linkHandler: (row) => {
      // Ensure multiple statements are handled properly
      let matchingDetail = OrderDetail.find(
        (detail) => detail?.productId === row?.productId
      );
      matchingDetail = {
        ...matchingDetail, // Spread existing properties
        ...(orderId && { orderId: orderId?.orderId }), // Add orderId if it exists
        ...(orderId && { orderType: orderId?.orderType }), // Add orderType if it exists
      };
      if (matchingDetail) {
        dispatch(setItemDetail(matchingDetail));
        navigate(`/orders/item/${row?.item}`, {
          state: { itemDetails: matchingDetail }, // Pass object with navigate
        });
      } else {
        console.error("No matching detail found for item:", row?.item);
      }
    },
    linkLabel: (row) => row?.item || '—',
  },
  productId: {
    cellData: ({ row: { original } = {} }) => (
      <LinkText
        text={original?.productId}
        id={`${original?.productId} ${original?.productId}`}
        handleClick={() => navigate(`/products/product-detail/${original?.productId}`)}
      />
    ),
    linkHandler: (row) => navigate(`/products/product-detail/${row?.productId}`),
    linkLabel: (row) => row?.productId || '—',
  },
});

/**
 * Extracts specific properties from the OrderDetail array.
 * @param {Array} orderDetail - The original OrderDetail array.
 * @returns {Array} - A new array containing objects with only the specified properties.
 */
const extractOrderDetailProperties = (orderDetail) => {
  return orderDetail?.map((item) => ({
    type: item?.type,
    item: item?.item,
    productId: item?.productId,
    requestedQuantity: item?.requestedQuantity, 
    confirmedQuantity: item?.confirmedQuantity,
    changed: item?.changed,
    unitPrice: item?.unitPrice,
    materialUom: item?.materialUom,
    grandTotal: item?.grandTotal,
  })).sort((a, b) => {
    // Sort by productId in ascending order
    if (a?.productId < b?.productId) return -1;
    if (a?.productId > b?.productId) return 1;
    return 0;
  });
};

const OnlineItemsList = ({OrderDetail, orderId}) => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  
  const extractedOrderDetails = extractOrderDetailProperties(OrderDetail);

  // Use the reusable function to create the cellDataMapperObj
  const cellDataMapperObj = createCellDataMapperObj(navigate, OrderDetail, dispatch, orderId)

  const [rowSelected, setRowSelected] = useState([])
  const [isSelectAll, setIsSelectAll] = useState(false)
  const {
    OnlineItemListColConfigData,
    allRecords,
    setPageIndex,
    setSortingObj,
    setSearchArry,
    pageIndex,
    sortedData,
    handleResetAll 
  } = useItemList(extractedOrderDetails)

  const cellDataMappedCols = getCellDataMappedCols(cellDataMapperObj, OnlineItemListColConfigData)
 
  const dataToDownload = getDataToDownload(OnlineItemListColConfigData, isSelectAll ? extractedOrderDetails : rowSelected)

  const onDownloadClick = async () => {
    downloadDataAsCSVFile(dataToDownload, `OrderItem_${getDateWithTimeString()}`)
  }

  return (
    <div className="orderContainer items flex flex-col">
      <div className="relative mt-4 p-5" style={{backgroundColor: '#1D1D1D', borderRadius: '8px'}}>
        <div className="flex flex-row justify-between">
          <HeaderTitle title={'Items'} className="justify-start text-3xl font-bold text-slate-200" />
          <ButtonIcon icon="settings" size="medium" className="setting-icon" />
        </div>

        {extractedOrderDetails?.length > 0 && extractedOrderDetails && (
           <CustomDataTable
              columns={cellDataMappedCols}
              records={allRecords || []}
              pageSize={10}
              totalRecords={sortedData?.length|| 0}
              setPageIndex={setPageIndex}
              handleSorting={setSortingObj}
              setSearchArry={setSearchArry}
              onResetAll={handleResetAll}
              onRowSelect={(selectedRecords, isAll) => {
                setRowSelected(selectedRecords)
                setIsSelectAll(isAll)
              }}
              onDownload={onDownloadClick}
              showDownload={true}
              isSelectAll={isSelectAll}
              pageIndex={pageIndex}
          />
        )}
        {sortedData?.length && (
          <div className="txtRightAlign">{sortedData?.length} total items</div>
        )}
      </div>
    </div>
  )
}

export default OnlineItemsList
